import React, { useState, useEffect, useCallback } from 'react';
import { theme, fontStack, bodyFont } from '../theme';
import { FadeIn, Divider } from '../components/shared';
import churchConfig from '../churchConfig';
import { useChurchData, useChurchInfo } from '../data/ChurchDataProvider';

/* ═══════════════════════════════════════════
   YouTube Integration — Three-tier approach:
   1. If YOUTUBE_CHANNEL_ID is set → fetch RSS feed via CORS proxy
   2. If only handle is set → resolve channel ID at runtime, then fetch RSS
   3. Fallback → embed the channel's uploads playlist directly
   ═══════════════════════════════════════════ */

// Resolve a @handle to a channel ID by fetching the channel page through a CORS proxy
async function resolveChannelId(handle) {
  const url = `https://www.youtube.com/@${handle}`;
  const proxies = [
    `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    `https://corsproxy.io/?${encodeURIComponent(url)}`,
  ];
  for (const proxy of proxies) {
    try {
      const res = await fetch(proxy);
      if (!res.ok) continue;
      const html = await res.text();
      // Look for channel ID in page source
      const patterns = [
        /"channelId":"(UC[a-zA-Z0-9_-]+)"/,
        /"externalId":"(UC[a-zA-Z0-9_-]+)"/,
        /channel_id=(UC[a-zA-Z0-9_-]+)/,
        /\/channel\/(UC[a-zA-Z0-9_-]+)/,
        /"browseId":"(UC[a-zA-Z0-9_-]+)"/,
      ];
      for (const pat of patterns) {
        const match = html.match(pat);
        if (match) return match[1];
      }
    } catch (err) {
      console.warn('Channel resolve failed:', err.message);
    }
  }
  return null;
}

async function fetchYouTubeVideos(channelId) {
  const feedUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;
  const proxies = [
    `https://api.allorigins.win/raw?url=${encodeURIComponent(feedUrl)}`,
    `https://corsproxy.io/?${encodeURIComponent(feedUrl)}`,
  ];
  for (const proxy of proxies) {
    try {
      const res = await fetch(proxy);
      if (!res.ok) continue;
      const text = await res.text();
      return parseRSSFeed(text);
    } catch (err) { console.warn('RSS fetch failed:', err.message); }
  }
  return null;
}

function parseRSSFeed(xmlText) {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlText, "text/xml");
  const entries = xml.querySelectorAll("entry");
  const videos = [];
  entries.forEach((entry) => {
    const id = entry.getElementsByTagName("yt:videoId")[0]?.textContent
      || entry.querySelector("id")?.textContent?.replace("yt:video:", "");
    const title = entry.querySelector("title")?.textContent || "Untitled";
    const published = entry.querySelector("published")?.textContent || "";
    const descNode = entry.getElementsByTagName("media:description")[0] || entry.querySelector("description");
    const desc = descNode?.textContent || "";
    if (id) {
      videos.push({ id, title, published, description: desc.substring(0, 150) + (desc.length > 150 ? "..." : ""), date: formatDate(published), category: categorizeVideo(title) });
    }
  });
  return videos;
}

function formatDate(iso) {
  if (!iso) return "Recent";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "Recent"; }
}

function categorizeVideo(title) {
  const t = title.toLowerCase();
  if (t.includes("worship") || t.includes("praise") || t.includes("song") || t.includes("choir") || t.includes("hymn") || t.includes("music")) return "worship";
  if (t.includes("event") || t.includes("anniversary") || t.includes("revival") || t.includes("conference") || t.includes("celebration")) return "events";
  if (t.includes("bible study") || t.includes("wednesday")) return "study";
  return "sermons";
}

export default function ResourcesPage() {
  const { data } = useChurchData();
  const info = useChurchInfo();
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channelId, setChannelId] = useState(info.youtubeChannelId || "");
  const [mode, setMode] = useState("loading"); // "rss" | "playlist" | "loading"

  const loadVideos = useCallback(async (cId) => {
    if (!cId) return false;
    const fetched = await fetchYouTubeVideos(cId);
    if (fetched && fetched.length > 0) {
      setVideos(fetched);
      setActiveVideo(fetched[0].id);
      setMode("rss");
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    async function init() {
      setLoading(true);

      // Tier 1: Direct channel ID
      if (info.youtubeChannelId) {
        const ok = await loadVideos(info.youtubeChannelId);
        if (ok) { setChannelId(info.youtubeChannelId); setLoading(false); return; }
      }

      // Tier 2: Resolve from handle
      if (info.youtubeHandle) {
        const resolved = await resolveChannelId(info.youtubeHandle);
        if (resolved) {
          console.log("Resolved channel ID:", resolved);
          setChannelId(resolved);
          const ok = await loadVideos(resolved);
          if (ok) { setLoading(false); return; }
        }
      }

      // Tier 3: Playlist embed mode (always works, no API needed)
      setMode("playlist");
      setLoading(false);
    }
    init();
  }, [loadVideos]);

  // Uploads playlist ID = replace "UC" prefix with "UU"
  const uploadsPlaylistId = channelId ? "UU" + channelId.substring(2) : "";

  const tabs = [
    { key: "all", label: "All Videos" },
    { key: "sermons", label: "Sermons" },
    { key: "worship", label: "Worship" },
    { key: "study", label: "Bible Study" },
    { key: "events", label: "Events" },
  ];

  const filtered = activeTab === "all" ? videos : videos.filter(v => v.category === activeTab);

  return (
    <div style={{ paddingTop: 100 }}>
      {/* Header */}
      <section style={{ padding: "80px 24px 40px", textAlign: "center" }}>
        <FadeIn>
          <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 16 }}>Media Library</div>
          <h1 style={{ fontFamily: fontStack, fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 300, color: theme.cream, lineHeight: 1.1 }}>
            <span style={{ fontStyle: "italic", fontWeight: 600, color: theme.gold }}>Resources</span> & Media
          </h1>
          <Divider width={60} />
          <p style={{ fontFamily: bodyFont, fontSize: 16, color: theme.textMuted, maxWidth: 500, margin: "16px auto 0", lineHeight: 1.7 }}>
            Watch sermons, worship sessions, and event recordings from our YouTube channel.
          </p>
        </FadeIn>
      </section>

      {/* ── PLAYLIST EMBED MODE (always works) ── */}
      {mode === "playlist" && !loading && (
        <section style={{ padding: "0 24px 60px" }}>
          <FadeIn>
            <div style={{ maxWidth: 960, margin: "0 auto" }}>
              {/* Full channel embed — works with just the handle, no API needed */}
              <div style={{
                position: "relative", paddingBottom: "56.25%", height: 0,
                borderRadius: 4, overflow: "hidden",
                border: `1px solid ${theme.border}`,
                boxShadow: `0 20px 60px rgba(0,0,0,0.5)`,
              }}>
                {uploadsPlaylistId ? (
                  <iframe
                    src={`https://www.youtube.com/embed/videoseries?list=${uploadsPlaylistId}&rel=0&modestbranding=1`}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen title="Church Videos"
                  />
                ) : (
                  <iframe
                    src={`https://www.youtube.com/embed?listType=user_uploads&list=${info.youtubeHandle}&rel=0&modestbranding=1`}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen title="Church Videos"
                  />
                )}
              </div>
              <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
                Showing all uploads from our YouTube channel. Use the playlist controls in the player to browse videos.
              </p>
            </div>
          </FadeIn>
        </section>
      )}

      {/* ── RSS FEED MODE (individual video cards) ── */}
      {mode === "rss" && !loading && (
        <>
          {/* Main Player */}
          <section style={{ padding: "0 24px 40px" }}>
            <FadeIn>
              <div style={{ maxWidth: 960, margin: "0 auto" }}>
                <div style={{
                  position: "relative", paddingBottom: "56.25%", height: 0,
                  borderRadius: 4, overflow: "hidden",
                  border: `1px solid ${theme.border}`,
                  boxShadow: `0 20px 60px rgba(0,0,0,0.5)`,
                }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${activeVideo}?rel=0&modestbranding=1`}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen title="Video Player"
                  />
                </div>
              </div>
            </FadeIn>
          </section>

          {/* Tabs + Grid */}
          <section style={{ padding: "20px 24px 60px" }}>
            <div style={{ maxWidth: 960, margin: "0 auto" }}>
              <FadeIn>
                <div style={{ display: "flex", gap: 4, marginBottom: 32, justifyContent: "center", flexWrap: "wrap" }}>
                  {tabs.map(t => {
                    const count = t.key === "all" ? videos.length : videos.filter(v => v.category === t.key).length;
                    if (t.key !== "all" && count === 0) return null;
                    return (
                      <button key={t.key} onClick={() => setActiveTab(t.key)}
                        style={{
                          padding: "12px 28px",
                          background: activeTab === t.key ? theme.gold : "transparent",
                          border: `1px solid ${activeTab === t.key ? theme.gold : theme.border}`,
                          color: activeTab === t.key ? theme.bg : theme.textMuted,
                          fontFamily: bodyFont, fontSize: 12, fontWeight: 600,
                          letterSpacing: "0.12em", textTransform: "uppercase",
                          cursor: "pointer", transition: "all 0.3s",
                        }}
                        onMouseEnter={e => { if (activeTab !== t.key) e.target.style.borderColor = theme.gold; }}
                        onMouseLeave={e => { if (activeTab !== t.key) e.target.style.borderColor = theme.border; }}
                      >{t.label} ({count})</button>
                    );
                  })}
                </div>
              </FadeIn>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
                {filtered.map((v, i) => (
                  <FadeIn key={`${v.id}-${i}`} delay={i * 0.08}>
                    <div
                      onClick={() => { setActiveVideo(v.id); window.scrollTo({ top: 200, behavior: "smooth" }); }}
                      style={{
                        cursor: "pointer", overflow: "hidden",
                        background: theme.bgCard, border: `1px solid ${activeVideo === v.id ? theme.gold : theme.border}`,
                        transition: "all 0.4s ease",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = theme.gold; e.currentTarget.style.transform = "translateY(-4px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = activeVideo === v.id ? theme.gold : theme.border; e.currentTarget.style.transform = "translateY(0)"; }}
                    >
                      <div style={{ position: "relative", paddingBottom: "56.25%", background: `url(https://img.youtube.com/vi/${v.id}/hqdefault.jpg) center/cover` }}>
                        <div style={{ position: "absolute", inset: 0, background: "rgba(10,10,15,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <div style={{ width: 50, height: 50, borderRadius: "50%", background: "rgba(200,164,86,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill={theme.bg}><polygon points="6,3 18,10 6,17" /></svg>
                          </div>
                        </div>
                        {activeVideo === v.id && (
                          <div style={{ position: "absolute", top: 8, right: 8, padding: "4px 10px", background: theme.gold, fontFamily: bodyFont, fontSize: 9, fontWeight: 700, color: theme.bg, letterSpacing: "0.1em", textTransform: "uppercase" }}>Now Playing</div>
                        )}
                        <div style={{ position: "absolute", bottom: 8, left: 8, padding: "3px 8px", background: "rgba(10,10,15,0.7)", fontFamily: bodyFont, fontSize: 9, fontWeight: 600, color: theme.gold, letterSpacing: "0.1em", textTransform: "uppercase", backdropFilter: "blur(4px)" }}>{v.category}</div>
                      </div>
                      <div style={{ padding: 20 }}>
                        <div style={{ fontFamily: bodyFont, fontSize: 11, color: theme.gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{v.date}</div>
                        <h4 style={{ fontFamily: fontStack, fontSize: 18, fontWeight: 500, color: theme.cream, marginBottom: 6, lineHeight: 1.3 }}>{v.title}</h4>
                        {v.description && <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, lineHeight: 1.6 }}>{v.description}</p>}
                      </div>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Loading */}
      {loading && (
        <section style={{ padding: "40px 24px 80px", textAlign: "center" }}>
          <div style={{ width: 40, height: 40, border: `2px solid ${theme.border}`, borderTopColor: theme.gold, borderRadius: "50%", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted }}>Connecting to YouTube...</p>
        </section>
      )}

      {/* Channel Link */}
      <section style={{ padding: "20px 24px 80px" }}>
        <FadeIn delay={0.3}>
          <div style={{ textAlign: "center" }}>
            <a href={`https://www.youtube.com/@${info.youtubeHandle}`} target="_blank" rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                padding: "14px 32px", background: "transparent",
                border: `1px solid ${theme.border}`, color: theme.cream,
                textDecoration: "none", fontFamily: bodyFont, fontSize: 13,
                fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", transition: "all 0.3s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#FF0000"; e.currentTarget.style.color = "#FF4444"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.color = theme.cream; }}
            >
              <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
                <path d="M19.6 2.2C19.4 1.4 18.7.7 17.9.5 16.3 0 10 0 10 0S3.7 0 2.1.5C1.3.7.6 1.4.4 2.2 0 3.8 0 7 0 7s0 3.2.4 4.8c.2.8.9 1.5 1.7 1.7C3.7 14 10 14 10 14s6.3 0 7.9-.5c.8-.2 1.5-.9 1.7-1.7C20 10.2 20 7 20 7s0-3.2-.4-4.8zM8 10V4l5.2 3L8 10z" />
              </svg>
              Visit Our YouTube Channel
            </a>
          </div>
        </FadeIn>
      </section>

      {/* ── PHOTO GALLERY ── */}
      {data.mediaEnabled && (data.photos || []).filter(p => p.enabled).length > 0 && (
        <PhotoGallery photos={(data.photos || []).filter(p => p.enabled)} />
      )}

      {/* ── DOCUMENT LIBRARY ── */}
      {data.mediaEnabled && (data.documents || []).filter(d => d.enabled && !d.staffOnly).length > 0 && (
        <section style={{ padding: "0 24px 80px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <FadeIn>
              <div style={{ textAlign: "center", marginBottom: 40 }}>
                <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Downloads</div>
                <h2 style={{ fontFamily: fontStack, fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 400, color: theme.cream }}>Documents & Resources</h2>
                <Divider />
              </div>
            </FadeIn>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
              {(data.documents || []).filter(d => d.enabled && !d.staffOnly).map((doc, i) => (
                <FadeIn key={doc.id} delay={i * 0.08}>
                  <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" download={doc.fileName || true}
                    style={{
                      display: "block", padding: 20, background: theme.bgCard,
                      border: `1px solid ${theme.border}`, textDecoration: "none",
                      transition: "all 0.3s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = theme.gold}
                    onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                      <span style={{ fontSize: 28, flexShrink: 0 }}>
                        {doc.fileType === "pdf" ? "📄" : doc.fileType === "doc" || doc.fileType === "docx" ? "📝" : "📎"}
                      </span>
                      <div>
                        <h4 style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.cream, marginBottom: 4 }}>{doc.title}</h4>
                        {doc.description && <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, lineHeight: 1.5 }}>{doc.description}</p>}
                        <span style={{ fontFamily: bodyFont, fontSize: 11, color: theme.gold, marginTop: 8, display: "inline-block" }}>
                          ⬇ Download {doc.fileType ? `.${doc.fileType}` : ""}
                        </span>
                      </div>
                    </div>
                  </a>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* ── Photo Gallery Component ── */
function PhotoGallery({ photos }) {
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "slideshow"
  const [slideIndex, setSlideIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  // Auto-advance slideshow
  useEffect(() => {
    if (viewMode !== "slideshow" || photos.length <= 1) return;
    const timer = setInterval(() => {
      setSlideIndex(prev => (prev + 1) % photos.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [viewMode, photos.length]);

  return (
    <section style={{ padding: "0 24px 80px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Gallery</div>
              <h2 style={{ fontFamily: fontStack, fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 400, color: theme.cream }}>Church Photos</h2>
              <Divider />
            </div>
          </div>
          {/* View toggle */}
          <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 24 }}>
            {["grid", "slideshow"].map(mode => (
              <button key={mode} onClick={() => setViewMode(mode)}
                style={{
                  padding: "8px 20px", fontFamily: bodyFont, fontSize: 11, fontWeight: 600,
                  letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer",
                  background: viewMode === mode ? theme.gold : "transparent",
                  border: `1px solid ${viewMode === mode ? theme.gold : theme.border}`,
                  color: viewMode === mode ? theme.bg : theme.textMuted,
                  transition: "all 0.3s",
                }}
              >{mode === "grid" ? "⊞ Grid" : "▶ Slideshow"}</button>
            ))}
          </div>
        </FadeIn>

        {/* Grid mode */}
        {viewMode === "grid" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {photos.map((photo, i) => (
              <FadeIn key={photo.id} delay={i * 0.05}>
                <div
                  onClick={() => setLightbox(i)}
                  style={{
                    cursor: "pointer", position: "relative", paddingBottom: "75%",
                    background: `url(${photo.image}) center/cover`,
                    border: `1px solid ${theme.border}`, overflow: "hidden",
                    transition: "all 0.3s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = theme.gold; e.currentTarget.style.transform = "scale(1.02)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.transform = "scale(1)"; }}
                >
                  {photo.caption && (
                    <div style={{
                      position: "absolute", bottom: 0, left: 0, right: 0,
                      padding: "20px 10px 8px", background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
                      fontFamily: bodyFont, fontSize: 11, color: theme.cream,
                    }}>{photo.caption}</div>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        )}

        {/* Slideshow mode */}
        {viewMode === "slideshow" && photos.length > 0 && (
          <div style={{ position: "relative" }}>
            <div style={{
              width: "100%", paddingBottom: "56.25%", position: "relative",
              background: `url(${photos[slideIndex].image}) center/contain no-repeat`,
              backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`,
              cursor: "pointer",
            }}
              onClick={() => setLightbox(slideIndex)}
            />
            {photos[slideIndex].caption && (
              <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, textAlign: "center", marginTop: 12 }}>
                {photos[slideIndex].caption}
              </p>
            )}
            {/* Nav arrows */}
            {photos.length > 1 && (
              <>
                <button onClick={() => setSlideIndex((slideIndex - 1 + photos.length) % photos.length)}
                  style={{ position: "absolute", top: "50%", left: 8, transform: "translateY(-50%)", background: "rgba(0,0,0,0.6)", border: "none", color: theme.cream, fontSize: 24, padding: "8px 14px", cursor: "pointer" }}>‹</button>
                <button onClick={() => setSlideIndex((slideIndex + 1) % photos.length)}
                  style={{ position: "absolute", top: "50%", right: 8, transform: "translateY(-50%)", background: "rgba(0,0,0,0.6)", border: "none", color: theme.cream, fontSize: 24, padding: "8px 14px", cursor: "pointer" }}>›</button>
              </>
            )}
            {/* Dots */}
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16 }}>
              {photos.map((_, i) => (
                <button key={i} onClick={() => setSlideIndex(i)}
                  style={{
                    width: 8, height: 8, borderRadius: "50%", border: "none", cursor: "pointer",
                    background: i === slideIndex ? theme.gold : "rgba(255,255,255,0.15)",
                    transition: "all 0.3s",
                  }} />
              ))}
            </div>
          </div>
        )}

        {/* Lightbox */}
        {lightbox !== null && (
          <div onClick={() => setLightbox(null)}
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000,
              background: "rgba(0,0,0,0.95)", display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "zoom-out",
            }}>
            <img src={photos[lightbox].image} alt={photos[lightbox].caption || ""}
              style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }}
              onClick={e => e.stopPropagation()}
            />
            {photos[lightbox].caption && (
              <p style={{ position: "absolute", bottom: 32, fontFamily: bodyFont, fontSize: 15, color: theme.cream, textAlign: "center" }}>
                {photos[lightbox].caption}
              </p>
            )}
            <button onClick={() => setLightbox(null)}
              style={{ position: "absolute", top: 20, right: 24, background: "none", border: "none", color: theme.gold, fontSize: 32, cursor: "pointer" }}>✕</button>
            {photos.length > 1 && (
              <>
                <button onClick={e => { e.stopPropagation(); setLightbox((lightbox - 1 + photos.length) % photos.length); }}
                  style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: theme.cream, fontSize: 40, cursor: "pointer" }}>‹</button>
                <button onClick={e => { e.stopPropagation(); setLightbox((lightbox + 1) % photos.length); }}
                  style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: theme.cream, fontSize: 40, cursor: "pointer" }}>›</button>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
